import ResponsiveContainer from "./DensityResponsiveContainer";
import { viewportHeight as layoutViewportHeight, viewportScrollY } from "../utils/displayDensity";
import { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Line, LineChart } from "recharts";
import { SkeletonText } from "./skeletons/SkeletonLoader";
import TikTokMetadataPopout from "./TikTokMetadataPopout";
import "./tiktokTable.css";

const TableSkeleton = ({ columnCount = 9 }) => (
  <tr style={{ borderBottom: "1px solid #333" }}>
    {Array.from({ length: columnCount }).map((_, j) => (
      <td key={j} style={{ padding: "6px 10px" }}>
        <SkeletonText width="100%" height={16} />
      </td>
    ))}
  </tr>
);

const deltaOverHours = (history, hours, field = "views") => {
  if (!Array.isArray(history) || !history.length) return 0;

  const sorted = [...history].sort((a, b) => (a.timeMs || 0) - (b.timeMs || 0));
  const latest = sorted[sorted.length - 1];
  const latestVal = Number(latest?.[field] || 0);
  const cutoff = (latest?.timeMs || 0) - hours * 60 * 60 * 1000;

  let baseRow = sorted[0];
  for (let i = sorted.length - 1; i >= 0; i--) {
    if ((sorted[i].timeMs || 0) <= cutoff) {
      baseRow = sorted[i];
      break;
    }
  }

  const baseVal = Number(baseRow?.[field] || 0);
  return latestVal - baseVal;
};

const VirtualizedTable = ({
  globalLoading,
  loading,
  skeletonRowCount,
  filtered,
  totalViews,
  comparisonGames,
  setComparisonGames,
  onToggleComparisonGame,
  setShowInsights,
  rankingMetric,
  rankingWindow,
  variant = "default",
}) => {
  const isTikTok = variant === "tiktok";
  const dataMetric = rankingMetric === "videos" ? "videos" : "views";
  const stickyTableRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [sortKey, setSortKey] = useState("views");
  const [tableMetadata, setTableMetadata] = useState(null); // { key, game, x, y, side }
  useLayoutEffect(() => {
    if (!isTikTok || !stickyHeaderRef.current) return undefined;
    const dashboard = stickyHeaderRef.current.closest(".mg-jura-dashboard");
    const rankingHeader = dashboard?.querySelector(".mg-tiktok-ranking-header");
    if (!rankingHeader) return undefined;
    const appHeader = document.querySelector(".mg-app-header");

    const updateOffset = () => {
      const appHeaderHeight = appHeader?.getBoundingClientRect().height ?? 70;
      stickyHeaderRef.current?.style.setProperty(
        "--tt-table-sticky-top",
        `${appHeaderHeight + rankingHeader.getBoundingClientRect().height}px`
      );
      rankingHeader.style.setProperty("--tt-selector-sticky-top", `${appHeaderHeight}px`);
    };
    updateOffset();
    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(updateOffset)
      : null;
    if (observer) {
      observer.observe(rankingHeader);
      if (appHeader) observer.observe(appHeader);
    }
    else window.addEventListener("resize", updateOffset);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateOffset);
      rankingHeader.style.removeProperty("--tt-selector-sticky-top");
    };
  }, [isTikTok]);
  useEffect(() => {
    if (rankingMetric) {
      setSortKey(rankingMetric === "momentum"
        ? "momentumRank"
        : rankingWindow === "latest" ? rankingMetric : "ranking");
    }
  }, [rankingMetric, rankingWindow]);

  const calculateVisibleCount = () => {
    const rowHeight = 42;
    const rowCount = Math.ceil(layoutViewportHeight() / rowHeight);
    return rowCount + 10;
  };

  useEffect(() => {
    if (filtered.length > 0) {
      setVisibleCount(calculateVisibleCount());
    }
  }, [filtered]);

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(calculateVisibleCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (
        layoutViewportHeight() + viewportScrollY() >=
        document.documentElement.offsetHeight - 120
      ) {
        setVisibleCount((prev) => Math.min(prev + 20, filtered.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [filtered.length]);

  const enriched = useMemo(() => {
    return filtered.map((game) => {
      const history = Array.isArray(game.history) ? game.history : [];
      const lastDelta =
        history.length > 0 ? Number(history[history.length - 1].deltaViews || 0) : 0;

      const oneDayChange = game.window_1d ? game.window_1d.available ? game.window_1d[dataMetric] : null : deltaOverHours(history, 24, dataMetric);
      const sevenDayChange = game.window_7d ? game.window_7d.available ? game.window_7d[dataMetric] : null : deltaOverHours(history, 24 * 7, dataMetric);
      const shareValue = game.rankedMetrics ? game[dataMetric === "videos" ? "videosTotal" : "viewsTotal"] : game[dataMetric];
      const share =
        totalViews > 0 ? (Number(shareValue || 0) / totalViews) * 100 : 0;

      return {
        game,
        history,
        lastDelta,
        oneDayChange,
        sevenDayChange,
        share,
      };
    });
  }, [filtered, totalViews, dataMetric]);

  const sorted = useMemo(() => {
    const rows = [...enriched];
    rows.sort((a, b) => {
      switch (sortKey) {
        case "ranking":
          return Number(b.game[rankingMetric === "videos" ? "videosTotal" : "viewsTotal"]) -
            Number(a.game[rankingMetric === "videos" ? "videosTotal" : "viewsTotal"]);
        case "videos":
          return (b.game.rankedMetrics && dataMetric === "videos" ? b.game.videosTotal || 0 : b.game.videos || 0) -
            (a.game.rankedMetrics && dataMetric === "videos" ? a.game.videosTotal || 0 : a.game.videos || 0);
        case "views":
          return (b.game.rankedMetrics ? b.game.viewsTotal || 0 : b.game.views || 0) -
            (a.game.rankedMetrics ? a.game.viewsTotal || 0 : a.game.views || 0);
        case "delta1d":
          return (b.oneDayChange || 0) - (a.oneDayChange || 0);
        case "delta7d":
          return (b.sevenDayChange || 0) - (a.sevenDayChange || 0);
        case "share":
          return (b.share || 0) - (a.share || 0);
        case "momentumRank":
          return (a.game.momentumRank ?? Infinity) - (b.game.momentumRank ?? Infinity);
        default:
          return 0;
      }
    });
    return rows;
  }, [enriched, sortKey, rankingMetric, dataMetric]);

  const visibleItems = loading ? [] : sorted.slice(0, visibleCount);

  const renderSortableHeader = (label, key, alignRight = false, direction = "desc") => {
    const active = sortKey === key || (sortKey === "ranking" && rankingMetric === key);
    return (
      <th
        onClick={() => setSortKey(key)}
        style={{
          padding: "6px 10px",
          textAlign: alignRight ? "right" : "left",
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            color: active ? "#0cf" : "#fff",
            fontWeight: active ? 800 : 600,
          }}
        >
          {label}
          {active && (
            <span
              style={{
                marginLeft: 4,
                fontSize: 11,
                verticalAlign: "middle",
              }}
            >
              {direction === "asc" ? "▲" : "▼"}
            </span>
          )}
        </span>
      </th>
    );
  };

  const renderTableHeader = () => (
    <thead>
      <tr style={{ backgroundColor: isTikTok ? "transparent" : "#222", textAlign: "left" }}>
        <th style={{ padding: "6px 10px", width: 40 }}>#</th>
        <th style={{ padding: "6px 10px", width: 320 }}>Game</th>
        {renderSortableHeader(dataMetric === "views" ? "Videos total" :
          rankingWindow && rankingWindow !== "latest" ? `${rankingWindow.toUpperCase()} Videos gained` : "Videos", "videos", true)}
        {renderSortableHeader(rankingWindow && rankingWindow !== "latest" ? `${rankingWindow.toUpperCase()} Views gained` : "Views", "views", true)}
        {renderSortableHeader("1D Change", "delta1d", true)}
        {renderSortableHeader("7D Change", "delta7d", true)}
        {renderSortableHeader("% Share", "share", true)}
        {isTikTok && renderSortableHeader("Momentum Rank", "momentumRank", true, "asc")}
        {!isTikTok && <th style={{ padding: "6px 10px", textAlign: "center" }}>Trend</th>}
        <th style={{ padding: "6px 10px", textAlign: isTikTok ? "center" : undefined }}>
          {isTikTok ? "Compare" : ""}
        </th>
      </tr>
    </thead>
  );

  return (
    <div style={{ padding: "20px 0" }}>
      {isTikTok && <div ref={stickyHeaderRef} className="mg-tiktok-table-sticky-header" aria-hidden="true">
        <div className="mg-tiktok-table-sticky-viewport">
          <table
            ref={stickyTableRef}
            className="mg-tiktok-table"
            style={{
              width: "100%",
              minWidth: 1100,
              tableLayout: "fixed",
              borderCollapse: "separate",
              color: "#fff",
              fontSize: 15,
            }}
          >
            <colgroup>
              <col style={{ width: 52 }} />
              <col style={{ width: 320 }} />
              <col style={{ width: 105 }} />
              <col style={{ width: 135 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 105 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 73 }} />
            </colgroup>
            {renderTableHeader()}
          </table>
        </div>
      </div>}
      <div
        className={isTikTok ? "mg-tiktok-table-wrap" : undefined}
        onScroll={isTikTok ? event => {
          if (stickyTableRef.current) {
            stickyTableRef.current.style.transform = `translateX(-${event.currentTarget.scrollLeft}px)`;
          }
        } : undefined}
      >
      <table
        className={isTikTok ? "mg-tiktok-table" : undefined}
        style={{
          width: "100%",
          minWidth: isTikTok ? 1100 : undefined,
          tableLayout: isTikTok ? "fixed" : undefined,
          borderCollapse: isTikTok ? "separate" : "collapse",
          color: "#fff",
          fontSize: 15,
        }}
      >
        {isTikTok && <colgroup>
          <col style={{ width: 52 }} />
          <col style={{ width: 320 }} />
          <col style={{ width: 105 }} />
          <col style={{ width: 135 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 105 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 73 }} />
        </colgroup>}
        {renderTableHeader()}
        <tbody>
          {globalLoading || loading
            ? Array.from({ length: skeletonRowCount }).map((_, idx) => (
                <TableSkeleton key={idx} />
              ))
            : visibleItems.map(({ game, history, oneDayChange, sevenDayChange, share }, i) => {
                const shareDisplay =
                  share >= 1 ? share.toFixed(2) : share.toFixed(3);
                const selectionKey = game.selectionKey || game.name;
                const groupSelectionKeys = game.groupSelectionKeys?.length
                  ? game.groupSelectionKeys
                  : [selectionKey];
                const compared = isTikTok
                  ? groupSelectionKeys.some(key => comparisonGames.includes(key)) || comparisonGames.includes(game.name)
                  : comparisonGames.includes(selectionKey);

                return (
                  <tr key={selectionKey} className={isTikTok ? "tt-table-row" : undefined}
                    style={{ borderBottom: "1px solid #333" }}>
                    <td style={{ padding: "6px 10px" }}>{i + 1}</td>

                    <td style={{ padding: "6px 10px" }}>
                      <div
                        className={isTikTok ? "tt-table-game-cell" : undefined}
                        style={isTikTok ? undefined : {
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          padding: "3px 10px",
                          borderRadius: 6,
                          overflow: "hidden",
                          background: "#050505",
                          minHeight: 50,
                        }}
                      >
                        {!isTikTok && game.imageUrl && (
                          <div
                            style={{
                              position: "absolute",
                              inset: -4,
                              backgroundImage: `url(${game.imageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              opacity: 0.22,
                              filter: "blur(3px)",
                              transform: "scale(1.06)",
                            }}
                          />
                        )}

                        <div
                          style={{
                            position: "relative",
                            flex: "0 0 auto",
                            marginRight: 10,
                          }}
                          onMouseEnter={isTikTok ? (event) => {
                            const rect = event.currentTarget.getBoundingClientRect();
                            const side = rect.right + 410 <= window.innerWidth
                              ? "right" : rect.left >= 410 ? "left" : "below";
                            const x = side === "right" ? rect.right + 10
                              : side === "left" ? rect.left - 410
                              : rect.left;
                            const y = side === "below" ? rect.bottom + 10 : rect.top;
                            setTableMetadata({ key: selectionKey, game, x, y, side });
                          } : undefined}
                          onMouseLeave={isTikTok ? () => setTableMetadata(null) : undefined}
                        >
                          {game.imageUrl ? (
                            <img
                              src={game.imageUrl}
                              alt={game.name}
                              style={{
                                height: 50,
                                width: "auto",
                                borderRadius: 4,
                                objectFit: "contain",
                                objectPosition: "center",
                                boxShadow: "0 0 6px rgba(0,0,0,0.7)",
                                display: "block",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 34,
                                height: 50,
                                borderRadius: 4,
                                background: "#222",
                              }}
                            />
                          )}
                        </div>

                        <div
                          style={{
                            position: "relative",
                            flex: 1,
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#f5f7fb",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          className={isTikTok ? "tt-table-game-name" : undefined}
                          title={game.displayName || game.name}
                        >
                          {game.displayName || game.name}
                          {game.coverageLabel && <small style={{ display: "block", color: "#f5e6c8" }}>{game.coverageLabel}</small>}
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      {game.rankedMetrics && dataMetric === "videos"
                        ? game.videosTotal == null ? "—" : Number(game.videosTotal).toLocaleString()
                        : Number(game.videos || 0).toLocaleString()}
                    </td>

                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      {game.rankedMetrics ? game.viewsTotal == null ? "—" : Number(game.viewsTotal).toLocaleString() : Number(game.views || 0).toLocaleString()}
                    </td>

                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      {oneDayChange == null ? "—" : Number(oneDayChange).toLocaleString()}
                    </td>

                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      {sevenDayChange == null ? "—" : Number(sevenDayChange).toLocaleString()}
                    </td>

                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      {shareDisplay}%
                    </td>

                    {isTikTok && <>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}
                        title={game.momentumIneligibleReason || "Experimental TikTok Momentum Rank"}>
                        {game.momentumRank == null ? "—" : game.momentumRank.toLocaleString()}
                      </td>
                    </>}

                    {!isTikTok && <td style={{ padding: "6px 10px", width: 100 }}>
                      <ResponsiveContainer width="100%" height={26}>
                        <LineChart data={history.slice(-30)}>
                          <Line
                            type="monotone"
                            dataKey="deltaViews"
                            stroke="#3fa9f5"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </td>}

                    <td style={{ padding: "6px 10px" }}>
                      {isTikTok ? (
                        <label className="tt-table-compare">
                          <input
                            type="checkbox"
                            checked={compared}
                            aria-label={compared
                              ? `Remove ${game.displayName || game.name} from comparison`
                              : `Add ${game.displayName || game.name} to comparison`}
                            onChange={() => {
                              setShowInsights(true);
                              if (onToggleComparisonGame) {
                                onToggleComparisonGame(game);
                                return;
                              }
                              if (!isTikTok) {
                                setComparisonGames(previous => previous.includes(selectionKey)
                                  ? previous.filter(key => key !== selectionKey)
                                  : [...previous, selectionKey]);
                                return;
                              }
                              setComparisonGames(previous => {
                                const isSelected = groupSelectionKeys.some(key => previous.includes(key)) ||
                                  previous.includes(game.name);
                                return isSelected
                                  ? previous.filter(key => !groupSelectionKeys.includes(key) && key !== game.name)
                                  : [...new Set([...previous, ...groupSelectionKeys])];
                              });
                            }}
                          />
                        </label>
                      ) : (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setShowInsights(true);
                          setComparisonGames((prev) =>
                            prev.includes(game.selectionKey || game.name)
                              ? prev.filter((g) => g !== (game.selectionKey || game.name))
                              : [...prev, game.selectionKey || game.name]
                          );
                        }}
                        style={{
                          background: comparisonGames.includes(game.selectionKey || game.name)
                            ? "#0cf"
                            : "#333",
                          color: "#fff",
                          border: "none",
                          padding: "6px 10px",
                          fontSize: 13,
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        {comparisonGames.includes(game.selectionKey || game.name)
                          ? "✔ In Compare"
                          : "+ Add to Compare"}
                      </button>
                      )}
                    </td>
                  </tr>
                );
              })}
        </tbody>
      </table>
      </div>
      {isTikTok && tableMetadata && createPortal(
        <div
          style={{ position: "fixed", left: tableMetadata.x, top: tableMetadata.y, zIndex: 11000 }}
          onMouseLeave={() => setTableMetadata(null)}
        >
          <TikTokMetadataPopout
            game={tableMetadata.game}
            side="inline"
            onMouseEnter={() => setTableMetadata(tableMetadata)}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default VirtualizedTable;